/* ─────────────────────────────────────────────────────────────
   VALUATION WIDGETS — building blocks for the Intrinsic Value tab

   • DataAsOfBlock  — the "Data as of …" line from the data_as_of payload
   • AnchorPicker   — selects fair-value anchors for the custom-mean endpoint
   • StatementTable — collapsible FY statement, projected columns flagged
───────────────────────────────────────────────────────────── */

import React, { useState } from "react";
import { Collapse, Tooltip as MuiTooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatCurrency, fmtL } from "../../../utils/currency";
import { buildStatementRows, isShareCountKey, normalizeShareCount } from "../../../config/statements";
import { FV_ANCHORS, inputLabel, fmtAsOf, decimalAsPct } from "../../../config/valuation";
import { Pill, Scroller, Table, Th, Td } from "./earningsNoteUI";

/* ══════════════════════════════════════════════════════════════
   DATA AS OF
══════════════════════════════════════════════════════════════ */
/* Which `data_as_of` inputs are RATES (decimals to render as percentages) and
   which are plain numbers. Beta is a regression coefficient, not a rate — it
   must render 0.10, never 10.00%, and a magnitude test alone gets that wrong
   because low-beta names sit well under 1. */
const RATE_INPUTS = new Set(["risk_free_rate", "equity_risk_premium"]);

const formatInputValue = (name, value) =>
  RATE_INPUTS.has(name) ? decimalAsPct(value, 2) : fmtL(value, 2);

export const DataAsOfBlock = ({ dataAsOf }) => {
  if (!dataAsOf) return null;
  const { valuation_date, inputs = [] } = dataAsOf;
  if (!valuation_date && inputs.length === 0) return null;

  return (
    <div
      style={{
        marginTop: "var(--spacing-sm)",
        color: "var(--color-text-muted)",
        fontSize: "var(--font-size-xs)",
        lineHeight: 1.7,
      }}
    >
      {valuation_date && (
        <span style={{ fontWeight: "var(--font-weight-semibold)" }}>
          Data as of — valued {fmtAsOf(valuation_date)}
          {inputs.length > 0 ? " · " : ""}
        </span>
      )}
      {inputs.map((inp, i) => (
        <span key={inp.name ?? i}>
          {i > 0 && <span style={{ opacity: 0.5 }}> · </span>}
          <span>
            {inputLabel(inp.name)}
            {inp.value != null && (
              <span style={{ color: "var(--color-text-secondary)" }}>
                {" "}
                {formatInputValue(inp.name, inp.value)}
              </span>
            )}{" "}
            <span style={{ opacity: 0.85 }}>({fmtAsOf(inp.as_of)})</span>
          </span>
        </span>
      ))}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   ANCHOR PICKER
   At least one anchor must stay selected — the endpoint treats an omitted
   `anchors` param as "use the engine's default trio", so an empty selection
   would silently return the trio instead of nothing.
══════════════════════════════════════════════════════════════ */
export const AnchorPicker = ({ selected, onChange, anchorState = {}, disabled }) => {
  const toggle = (name) => {
    const isOn = selected.includes(name);
    if (isOn && selected.length === 1) return; // keep at least one
    onChange(isOn ? selected.filter((n) => n !== name) : [...selected, name]);
  };

  return (
    <div style={{ display: "flex", gap: "var(--spacing-sm)", flexWrap: "wrap" }}>
      {FV_ANCHORS.map((a) => {
        const on = selected.includes(a.name);
        const isLast = on && selected.length === 1;
        // in_mean === false → selected but null for this stock
        const unavailable = on && anchorState[a.name] === false;
        return (
          <MuiTooltip
            key={a.name}
            title={
              unavailable
                ? `${a.hint} — not available for this stock, so it is skipped in the mean.`
                : isLast
                ? `${a.hint} — at least one anchor must stay selected.`
                : a.hint
            }
            placement="top"
            arrow
            enterDelay={300}
          >
            <button
              type="button"
              onClick={() => toggle(a.name)}
              disabled={disabled}
              aria-pressed={on}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 14px",
                borderRadius: "var(--radius-lg)",
                border: `1px solid ${on ? "var(--color-primary)" : "var(--color-border-primary)"}`,
                backgroundColor: on ? "var(--color-primary)" : "var(--color-surface)",
                color: on ? "#FFFFFF" : "var(--color-text-secondary)",
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-semibold)",
                cursor: disabled || isLast ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                transition: "var(--transition-fast)",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 15,
                  height: 15,
                  borderRadius: 3,
                  border: `1.5px solid ${on ? "#FFFFFF" : "var(--color-border-secondary)"}`,
                  fontSize: 11,
                  lineHeight: 1,
                  color: "#FFFFFF",
                }}
              >
                {on ? "✓" : ""}
              </span>
              {a.label}
              {unavailable && (
                <span style={{ fontSize: "var(--font-size-xs)", opacity: 0.85 }}>· n/a</span>
              )}
            </button>
          </MuiTooltip>
        );
      })}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   STATEMENT TABLE
══════════════════════════════════════════════════════════════ */

/** Compact plain number (no currency symbol) — for share counts. */
const compactCount = (v) => {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e12) return `${sign}${fmtL(abs / 1e12, 2)}T`;
  if (abs >= 1e9) return `${sign}${fmtL(abs / 1e9, 2)}B`;
  if (abs >= 1e6) return `${sign}${fmtL(abs / 1e6, 2)}M`;
  if (abs >= 1e3) return `${sign}${fmtL(abs / 1e3, 2)}K`;
  return `${sign}${fmtL(abs, 2)}`;
};

/**
 * ONE formatter for every cell in every statement, so the same kind of number
 * always reads the same way:
 *   percentages   → 10.3%
 *   share counts  → 284.70M   (unit-normalised first — see normalizeShareCount)
 *   per-share $   → $17.74
 *   everything    → $5.05B    (compact currency, 2 dp)
 */
const formatStatementValue = (row, v, period) => {
  if (v == null) return "—";
  if (row.isPercent) return decimalAsPct(v, 1);
  if (row.isShareCount ?? isShareCountKey(row.key))
    return compactCount(normalizeShareCount(v, { period, key: row.key }));
  if (row.isPerShare) return formatCurrency(v, { decimals: 2 });
  return formatCurrency(v, { compact: true, decimals: 2 });
};

export const StatementTable = ({ statement, periods, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  // Chronological left→right; the API does not guarantee an order.
  const sorted = React.useMemo(
    () =>
      [...(periods ?? [])].sort(
        (a, b) => (Number(a?.fiscal_year) || 0) - (Number(b?.fiscal_year) || 0)
      ),
    [periods]
  );

  const rows = React.useMemo(() => buildStatementRows(sorted, statement), [sorted, statement]);

  if (rows.length === 0) return null;

  const projectedCount = sorted.filter((p) => p?.is_projected).length;

  /* The CURRENT fiscal year is the last period the payload marks
     `is_projected: false` — never "the latest year" and never a hardcoded
     year, because the response returns the running FY as an actual while the
     years after it come back projected. */
  let currentIdx = -1;
  sorted.forEach((p, i) => {
    if (p && !p.is_projected) currentIdx = i;
  });

  return (
    <div
      style={{
        border: "1px solid var(--color-border-primary)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        backgroundColor: "var(--color-surface)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "12px 16px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            color: "var(--color-text-primary)",
            fontSize: "var(--font-size-base)",
            fontWeight: "var(--font-weight-bold)",
          }}
        >
          {statement.label}
          <Pill>
            {rows.length} line{rows.length === 1 ? "" : "s"} · {sorted.length} FY
          </Pill>
          {projectedCount > 0 && (
            <Pill tone="info">
              {projectedCount} projected
            </Pill>
          )}
        </span>
        <ExpandMoreIcon
          style={{
            fontSize: 22,
            color: "var(--color-text-muted)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "var(--transition-fast)",
            flexShrink: 0,
          }}
        />
      </button>

      <Collapse in={open} unmountOnExit>
        <Scroller style={{ border: "none", borderTop: "1px solid var(--color-border-primary)", borderRadius: 0 }}>
          <Table>
            <thead>
              <tr>
                <Th left style={{ position: "sticky", left: 0, zIndex: 2 }}>
                  Line item
                </Th>
                {sorted.map((p, i) => (
                  <Th key={`${p?.fiscal_year}-${i}`}>
                    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end" }}>
                      <span>FY{p?.fiscal_year ?? "—"}</span>
                      {p?.is_projected ? (
                        <span
                          style={{
                            color: "var(--color-primary)",
                            fontSize: 9,
                            letterSpacing: "0.06em",
                          }}
                        >
                          PROJ
                        </span>
                      ) : i === currentIdx ? (
                        <span
                          style={{
                            color: "var(--color-warning)",
                            fontSize: 9,
                            fontWeight: "var(--font-weight-bold)",
                            letterSpacing: "0.06em",
                          }}
                        >
                          CURRENT
                        </span>
                      ) : null}
                    </span>
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                /* Subtotal rows (the artifact's `total:1`) render bold with a
                   rule above AND a row-background highlight so they read as
                   section totals at a glance. */
                const subtotal = row.bold;
                return (
                  <tr key={row.key}>
                    <Td
                      left
                      bold={subtotal}
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        /* the sticky label cell needs its own opaque bg — it
                           must match the row highlight or the highlight would
                           vanish behind it on scroll */
                        backgroundColor: subtotal
                          ? "var(--color-surface-hover)"
                          : "var(--color-surface)",
                        fontWeight: subtotal
                          ? "var(--font-weight-bold)"
                          : "var(--font-weight-semibold)",
                        borderTop: subtotal
                          ? "1.5px solid var(--color-border-secondary)"
                          : undefined,
                      }}
                    >
                      {row.label}
                    </Td>
                    {row.values.map((v, i) => (
                      <Td
                        key={i}
                        bold={subtotal}
                        tone={sorted[i]?.is_projected ? "info" : "neutral"}
                        style={{
                          backgroundColor: subtotal
                            ? "var(--color-surface-hover)"
                            : sorted[i]?.is_projected
                            ? "rgba(0,150,255,0.05)"
                            : undefined,
                          borderTop: subtotal
                            ? "1.5px solid var(--color-border-secondary)"
                            : undefined,
                        }}
                      >
                        {formatStatementValue(row, v, sorted[i])}
                      </Td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Scroller>
      </Collapse>
    </div>
  );
};
